import { Box, FormControl, Menu, MenuItem, Select, Stack, Typography } from "@mui/material";
import { Suspense, useContext, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../context/ApiContext";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import { LanguageContext, LanguageMeta } from "../../context/LanguageContext";
import { UserContext } from "../../context/UserContext";
import { gaAction, gaCategory } from "../../utils/analytics";
import { getLanguageCode } from "../../utils/language";
import Button, { ButtonVariant } from "./Button";

const LanguageOption = ({ lang }: { lang: LanguageMeta }) => (
  <Stack direction="row" spacing={3} alignItems="center">
    <CircleFlag countryCode={lang.flag} height="20" />
    <Typography>{lang.name}</Typography>
  </Stack>
);

const useLanguageChange = () => {
  const { authApi } = useContext(ApiContext);
  const { analyticsEvent } = useContext(GoogleServicesContext);
  const languageCtx = useContext(LanguageContext);
  const { user, setUser } = useContext(UserContext);

  return (lang: LanguageMeta) => {
    analyticsEvent(gaCategory.language, gaAction.click, lang.code);
    languageCtx.setLanguage(lang.code);

    if (user) {
      const updatedLocale = getLanguageCode(lang.code);

      authApi
        .updateLocale({ value: updatedLocale })
        .then((user) => {
          setUser(user);
          analyticsEvent(gaCategory.language, gaAction.submit, "success");
        })
        .catch((error) => {
          analyticsEvent(gaCategory.language, gaAction.submit, "error");

          if (error?.response?.status === 401) {
            return;
          }

          error.response?.json().then((json: unknown) => {
            console.error(json);
          });
        });
    }
  };
};

export const LanguageSelector = () => {
  const { t } = useTranslation();
  const languageCtx = useContext(LanguageContext);
  const availableLanguages = languageCtx.availableLanguages;
  const language = availableLanguages?.find((lang) => lang.code === languageCtx.selectedLanguage);
  const languageLabel = t("header.language");
  const onChange = useLanguageChange();

  if (!availableLanguages || availableLanguages.length <= 1) {
    return null;
  }

  return (
    <Suspense fallback={<></>}>
      <Typography variant="h5" mb={3}>
        {languageLabel}
      </Typography>
      <FormControl sx={{ width: "100%" }}>
        <Select
          aria-label={languageLabel}
          value={language?.code ?? ""}
          renderValue={(value) => {
            const lang = availableLanguages.find((l) => l.code === value);
            return lang ? <LanguageOption lang={lang} /> : null;
          }}
          variant="outlined"
          onChange={(e) => {
            const selected = availableLanguages.find((l) => l.code === e.target.value);
            if (selected) onChange(selected);
          }}>
          {[...availableLanguages].reverse().map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <LanguageOption lang={lang} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Suspense>
  );
};

export const LanguageFlagButton = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const languageCtx = useContext(LanguageContext);
  const availableLanguages = languageCtx.availableLanguages;
  const language = availableLanguages?.find((lang) => lang.code === languageCtx.selectedLanguage);
  const languageLabel = t("header.language");
  const onChange = useLanguageChange();

  if (!availableLanguages || availableLanguages.length <= 1) return null;

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={
          <Suspense fallback={<Box sx={{ width: 20, height: 20 }} />}>
            <CircleFlag countryCode={language?.flag as string} height="20" />
          </Suspense>
        }
        variant={ButtonVariant.accent}
        aria-label={languageLabel}
      />
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 1 }}>
        {[...availableLanguages].map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === language?.code}
            onClick={() => {
              setAnchorEl(null);
              onChange(lang);
            }}>
            <LanguageOption lang={lang} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
